import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import useDebounce from "../../../../hooks/useDebounce";
import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { companyPermissionModules } from "../../../../utils/Constants";
import { canAddCompanyModule } from "../../../../utils/Permissions";
import { getCompanyClientsRequest } from "../../../../store/CompanyPortal/Clients/actions";
import { buildCompanyClientsListParams } from "../clientMappers";

const SEARCH_DEBOUNCE_MS = 300;

const useClientsList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const rows = useSelector((state) => state.companyClients.list);
  const meta = useSelector((state) => state.companyClients.meta);
  const isLoading = useSelector((state) => state.companyClients.isLoading);
  const login = useSelector((state) => state.auth.login);
  const { clients, shared } = messages[lang];
  const clientsModule = companyPermissionModules.manageClients;
  const canAdd = canAddCompanyModule(clientsModule, login);

  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, SEARCH_DEBOUNCE_MS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const listParams = useMemo(
    () =>
      buildCompanyClientsListParams({
        search: debouncedSearch,
        page,
        pageSize,
      }),
    [debouncedSearch, page, pageSize],
  );

  useEffect(() => {
    dispatch(getCompanyClientsRequest(listParams));
  }, [dispatch, listParams]);

  const totalClients = meta.count || 0;
  const paginationPages =
    meta.pages || Math.max(Math.ceil(totalClients / pageSize), 1);
  const paginationFrom = totalClients > 0 ? (page - 1) * pageSize + 1 : 0;
  const paginationTo = Math.min(page * pageSize, totalClients);
  const isSearchEmpty =
    !isLoading && totalClients === 0 && Boolean(debouncedSearch.trim());

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage);
  }, []);

  const handlePageSizeChange = useCallback((nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  }, []);

  const handleAddClient = useCallback(() => {
    history.push(ROUTE_PATHS.companyAddClient);
  }, [history]);

  const handleRowClick = useCallback(
    (client) => {
      if (!client?.id) return;

      history.push({
        pathname: ROUTE_PATHS.companyViewClient.replace(":id", client.id),
        state: { clientData: client },
      });
    },
    [history],
  );

  return {
    breadcrumbLabel:
      clients.listClients.listClientsBreadcrumb || clients.title,
    title: clients.title,
    totalClients,
    canAdd,
    addClientLabel: clients.addClient.createClient,
    searchPlaceholder: clients.searchPlaceholder,
    searchValue,
    onSearchChange: handleSearchChange,
    onAddClient: handleAddClient,
    rows,
    page,
    pageSize,
    paginationPages,
    paginationFrom,
    paginationTo,
    showingRangeTemplate: clients.showingRange,
    rowsPerPageLabel: shared.numberOfRows,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    onRowClick: handleRowClick,
    emptyTitle: clients.emptyTitle,
    emptyDescription: clients.emptyDescription,
    noDataDescription: clients.noDataDescription,
    isSearchEmpty,
    isLoading,
    tableLabels: {
      name: clients.listClients.name,
      email: clients.listClients.email,
      mobile: clients.listClients.mobile || clients.mobileNumber,
    },
  };
};

export default useClientsList;
